/* eslint-disable react/no-multi-comp -- техдолг: вспомогательные компоненты вынести в отдельные файлы (docs/todos/tech-debt/admin-page-helper-components.md) */
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
    AdminSpecialization,
    ProfessionalApplicationPublic,
    ProfessionalApplicationStatus,
    UserRole
} from '@cookspace-shared-contracts/core';
import { ArrowLeft, Check, Download, X } from 'lucide-react';

import {
    useApproveProfessionalApplicationMutation,
    useGetProfessionalApplicationQuery,
    useRejectProfessionalApplicationMutation
} from '@/api/professionalApplicationsApi';
import { useListAdminProfessionalSpecializationsQuery } from '@/api/professionalSpecializationsApi';
import { Modal } from '@/components/Modal';
import { SpecializationTree } from '@/components/SpecializationTree';
import { Textarea } from '@/components/Textarea';
import type {
    ProfessionalApplicationDocuments,
    SignedDocument
} from '@/lib/professionalApplicationsSchemas';

const REASON_MIN_LENGTH = 10;

const STATUS_LABEL: Record<ProfessionalApplicationStatus, string> = {
    draft: 'Черновик',
    pending: 'На рассмотрении',
    approved: 'Одобрена',
    rejected: 'Отклонена',
    withdrawn: 'Отозвана'
};

const STATUS_COLOR: Record<ProfessionalApplicationStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    withdrawn: 'bg-gray-100 text-gray-700'
};

const ROLE_LABEL: Record<UserRole, string> = {
    user: 'Пользователь',
    chef: 'Профессионал (модерация)',
    professional_chef: 'Профессионал (авто-публикация)'
};

function formatDateTime(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleString('ru-RU');
}

/**
 * Форматирует одобренные специализации для отображения в карточке решения.
 * Если есть `approvedSpecializations` — показываем имена через запятую, иначе прочерк.
 */
function formatApprovedSpecializations(application: ProfessionalApplicationPublic): string {
    if (
        application.approvedSpecializations &&
        application.approvedSpecializations.length > 0
    ) {
        return application.approvedSpecializations.map((s) => s.name).join(', ');
    }
    return '—';
}

interface DocumentLinkProps {
    label: string;
    doc: SignedDocument | null;
    testId: string;
}

function DocumentLink({ label, doc, testId }: DocumentLinkProps): ReactElement {
    if (doc === null) {
        return (
            <div
                className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                data-testid={`${testId}-empty`}
            >
                <span>{label}</span>
                <span className="text-xs">не загружено</span>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
            data-testid={testId}
            className="flex w-full items-center justify-between rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 transition hover:bg-blue-100"
        >
            <span className="inline-flex items-center gap-2">
                <Download size={14} />
                {label}
            </span>
            <span className="text-xs text-blue-600">
                до {formatDateTime(doc.expiresAt)}
            </span>
        </button>
    );
}

interface SpecializationsSelectorProps {
    items: AdminSpecialization[] | undefined;
    selectedIds: string[];
    onToggleSelected: (id: string) => void;
}

function SpecializationsSelector({
    items,
    selectedIds,
    onToggleSelected
}: SpecializationsSelectorProps): ReactElement {
    const noopSpec = (_node: AdminSpecialization): void => {
        // selectMode не использует
    };

    if (items === undefined) {
        return (
            <div className="px-3 py-4 text-sm text-gray-500">
                Загрузка специализаций...
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <div className="px-3 py-4 text-sm text-gray-500">
                Дерево специализаций пусто. Создайте узлы в разделе «Специализации».
            </div>
        );
    }
    return (
        <SpecializationTree
            items={items}
            onEdit={noopSpec}
            onDelete={noopSpec}
            onAddChild={noopSpec}
            draggable={false}
            selectMode
            selectedIds={selectedIds}
            onToggleSelected={onToggleSelected}
        />
    );
}

interface DocumentsListProps {
    documents: ProfessionalApplicationDocuments;
}

function DocumentsList({ documents }: DocumentsListProps): ReactElement {
    return (
        <div className="space-y-2" data-testid="professional-application-documents">
            <DocumentLink
                label="Скачать паспорт"
                doc={documents.passport}
                testId="professional-application-doc-passport"
            />
            <DocumentLink
                label="Скачать диплом"
                doc={documents.education}
                testId="professional-application-doc-education"
            />
            <DocumentLink
                label="Скачать опыт работы"
                doc={documents.experience}
                testId="professional-application-doc-experience"
            />
            {documents.additional.map((doc, idx) => (
                <DocumentLink
                    key={`additional-${idx}`}
                    label={`Доп. документ ${idx + 1}`}
                    doc={doc}
                    testId={`professional-application-doc-additional-${idx}`}
                />
            ))}
        </div>
    );
}

export function ProfessionalApplicationDetailPage(): ReactElement {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [isApproveOpen, setApproveOpen] = useState(false);
    const [approveRole, setApproveRole] = useState<'chef' | 'professional_chef'>('chef');
    const [approveSpecializationIds, setApproveSpecializationIds] = useState<string[]>([]);
    const [approveReason, setApproveReason] = useState('');
    const [approveValidationError, setApproveValidationError] = useState<string | null>(null);

    const [isRejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonError, setRejectReasonError] = useState<string | null>(null);

    const [actionError, setActionError] = useState<string | null>(null);

    const { data, isLoading, isError } = useGetProfessionalApplicationQuery(
        { id: id ?? '' },
        { skip: id === undefined || id === '' }
    );

    // Дерево специализаций нужно для multi-select при approve.
    // Skip — пока не открыт approve-модал.
    const { data: specializationsData } = useListAdminProfessionalSpecializationsQuery(
        undefined,
        { skip: !isApproveOpen }
    );

    const [approveApplication, { isLoading: isApproving }] =
        useApproveProfessionalApplicationMutation();
    const [rejectApplication, { isLoading: isRejecting }] =
        useRejectProfessionalApplicationMutation();

    if (id === undefined || id === '') {
        return (
            <div
                className="text-sm text-red-600"
                data-testid="professional-application-bad-id"
            >
                Не указан ID заявки
            </div>
        );
    }

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка заявки...</div>;
    }

    if (isError || !data) {
        return (
            <div
                className="text-sm text-red-600"
                data-testid="professional-application-error"
            >
                Не удалось загрузить заявку
            </div>
        );
    }

    const { application, user, documents } = data;

    const toggleSelected = (specId: string): void => {
        setApproveSpecializationIds((prev) => {
            if (prev.includes(specId)) {
                return prev.filter((id) => id !== specId);
            }
            return [...prev, specId];
        });
    };

    const openApprove = (): void => {
        setApproveRole('chef');
        // Pre-fill из desiredSpecializations.
        setApproveSpecializationIds(
            application.desiredSpecializations.map((s) => s.id)
        );
        setApproveReason('');
        setApproveValidationError(null);
        setActionError(null);
        setApproveOpen(true);
    };

    const closeApprove = (): void => {
        setApproveOpen(false);
        setApproveRole('chef');
        setApproveSpecializationIds([]);
        setApproveReason('');
        setApproveValidationError(null);
    };

    const openReject = (): void => {
        setRejectReason('');
        setRejectReasonError(null);
        setActionError(null);
        setRejectOpen(true);
    };

    const closeReject = (): void => {
        setRejectOpen(false);
        setRejectReason('');
        setRejectReasonError(null);
    };

    const submitApprove = async (): Promise<void> => {
        setApproveValidationError(null);
        if (approveSpecializationIds.length === 0) {
            setApproveValidationError(
                'Выберите минимум одну специализацию для одобрения'
            );
            return;
        }
        const reasonTrimmed = approveReason.trim();
        try {
            await approveApplication({
                id: application.id,
                body: {
                    role: approveRole,
                    approvedSpecializationIds: approveSpecializationIds,
                    ...(reasonTrimmed !== '' ? { reason: reasonTrimmed } : {})
                }
            }).unwrap();
            closeApprove();
        } catch {
            setActionError('Не удалось одобрить заявку');
            closeApprove();
        }
    };

    const submitReject = async (): Promise<void> => {
        const reasonTrimmed = rejectReason.trim();
        if (reasonTrimmed.length < REASON_MIN_LENGTH) {
            setRejectReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            return;
        }
        setRejectReasonError(null);
        try {
            await rejectApplication({
                id: application.id,
                body: { reason: reasonTrimmed }
            }).unwrap();
            closeReject();
        } catch {
            setActionError('Не удалось отклонить заявку');
            closeReject();
        }
    };

    const desiredSpecializationNames = application.desiredSpecializations
        .map((s) => s.name)
        .join(', ');

    return (
        <div data-testid="professional-application-detail-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/professional-applications')}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 transition hover:text-gray-900"
                    data-testid="professional-application-back"
                >
                    <ArrowLeft size={16} />
                    К списку
                </button>
                <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[application.status]}`}
                    data-testid="professional-application-status"
                >
                    {STATUS_LABEL[application.status]}
                </span>
            </div>

            <div className="rounded border border-gray-200 bg-white p-5 space-y-5">
                <section data-testid="professional-application-user">
                    <h2 className="mb-3 text-base font-semibold text-gray-900">
                        Пользователь
                    </h2>
                    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                        <div>
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Email
                            </dt>
                            <dd className="text-gray-900">{user?.email ?? '—'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Телефон
                            </dt>
                            <dd className="text-gray-900">{user?.phoneNumber ?? '—'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Имя
                            </dt>
                            <dd className="text-gray-900">{user?.displayName ?? '—'}</dd>
                        </div>
                    </dl>
                </section>

                <section data-testid="professional-application-info">
                    <h2 className="mb-3 text-base font-semibold text-gray-900">Заявка</h2>
                    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                        <div>
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Опыт (лет)
                            </dt>
                            <dd className="text-gray-900">{application.yearsOfExperience}</dd>
                        </div>
                        <div className="sm:col-span-2">
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Желаемые специализации
                            </dt>
                            <dd
                                className="text-gray-900"
                                data-testid="professional-application-desired-specializations"
                            >
                                {desiredSpecializationNames !== ''
                                    ? desiredSpecializationNames
                                    : '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Создана
                            </dt>
                            <dd className="text-gray-900">
                                {formatDateTime(application.createdAt)}
                            </dd>
                        </div>
                        <div className="sm:col-span-2">
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Публичная активность
                            </dt>
                            <dd
                                className="whitespace-pre-wrap text-gray-900"
                                data-testid="professional-application-public-activity"
                            >
                                {application.publicActivity ?? '—'}
                            </dd>
                        </div>
                        <div className="sm:col-span-2">
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Комментарий
                            </dt>
                            <dd
                                className="whitespace-pre-wrap text-gray-900"
                                data-testid="professional-application-comment"
                            >
                                {application.comment ?? '—'}
                            </dd>
                        </div>
                    </dl>
                </section>

                <section>
                    <h2 className="mb-3 text-base font-semibold text-gray-900">Документы</h2>
                    <DocumentsList documents={documents} />
                </section>

                {(application.status === 'approved' || application.status === 'rejected') && (
                    <section
                        className="rounded border border-gray-200 bg-gray-50 p-4"
                        data-testid="professional-application-decision"
                    >
                        <h2 className="mb-3 text-base font-semibold text-gray-900">Решение</h2>
                        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                            <div>
                                <dt className="text-xs font-medium uppercase text-gray-500">
                                    Дата решения
                                </dt>
                                <dd className="text-gray-900">
                                    {formatDateTime(application.reviewedAt)}
                                </dd>
                            </div>
                            {application.status === 'approved' && (
                                <>
                                    <div>
                                        <dt className="text-xs font-medium uppercase text-gray-500">
                                            Назначенный уровень доверия
                                        </dt>
                                        <dd
                                            className="text-gray-900"
                                            data-testid="professional-application-approved-role"
                                        >
                                            {application.approvedRole !== null
                                                ? ROLE_LABEL[application.approvedRole]
                                                : '—'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-xs font-medium uppercase text-gray-500">
                                            Одобренные специализации
                                        </dt>
                                        <dd
                                            className="text-gray-900"
                                            data-testid="professional-application-approved-specializations"
                                        >
                                            {formatApprovedSpecializations(application)}
                                        </dd>
                                    </div>
                                </>
                            )}
                            {application.status === 'rejected' && (
                                <div className="sm:col-span-2">
                                    <dt className="text-xs font-medium uppercase text-gray-500">
                                        Причина отклонения
                                    </dt>
                                    <dd
                                        className="whitespace-pre-wrap text-gray-900"
                                        data-testid="professional-application-rejection-reason"
                                    >
                                        {application.rejectionReason ?? '—'}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </section>
                )}
            </div>

            {actionError !== null && actionError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="professional-application-action-error"
                >
                    <span>{actionError}</span>
                    <button
                        type="button"
                        onClick={() => setActionError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {application.status === 'pending' && (
                <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                    <button
                        type="button"
                        onClick={openReject}
                        disabled={isApproving || isRejecting}
                        data-testid="professional-application-reject-button"
                        className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                    >
                        <X size={14} />
                        Отклонить
                    </button>
                    <button
                        type="button"
                        onClick={openApprove}
                        disabled={isApproving || isRejecting}
                        data-testid="professional-application-approve-button"
                        className="inline-flex items-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
                    >
                        <Check size={14} />
                        Одобрить
                    </button>
                </div>
            )}

            <Modal
                isOpen={isApproveOpen}
                onClose={closeApprove}
                title="Одобрить заявку"
                testId="professional-application-approve-modal"
            >
                <div
                    className="space-y-4"
                    data-testid="professional-application-approve-form"
                >
                    <div className="space-y-2 rounded border border-gray-200 p-3">
                        <p className="text-xs font-medium uppercase text-gray-500">
                            Уровень доверия
                        </p>
                        <label className="flex items-start gap-3 text-sm">
                            <input
                                type="radio"
                                name="approveRole"
                                checked={approveRole === 'chef'}
                                onChange={() => setApproveRole('chef')}
                                data-testid="professional-application-approve-role-chef"
                                className="mt-0.5"
                            />
                            <span>
                                <span className="font-medium text-gray-900">
                                    Профессионал (модерация)
                                </span>
                                <span className="block text-xs text-gray-600">
                                    Контент пользователя будет идти на модерацию
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 text-sm">
                            <input
                                type="radio"
                                name="approveRole"
                                checked={approveRole === 'professional_chef'}
                                onChange={() => setApproveRole('professional_chef')}
                                data-testid="professional-application-approve-role-professional"
                                className="mt-0.5"
                            />
                            <span>
                                <span className="font-medium text-gray-900">
                                    Профессионал (авто-публикация)
                                </span>
                                <span className="block text-xs text-gray-600">
                                    Контент публикуется автоматически без модерации
                                </span>
                            </span>
                        </label>
                    </div>

                    <div className="space-y-2">
                        <p className="text-xs font-medium uppercase text-gray-500">
                            Специализации
                        </p>
                        <p className="text-xs text-gray-500">
                            Выбрано: {approveSpecializationIds.length}. Заявитель указал:{' '}
                            {desiredSpecializationNames !== ''
                                ? desiredSpecializationNames
                                : '—'}
                            . Можно скорректировать набор.
                        </p>
                        <div
                            className="max-h-72 overflow-y-auto rounded border border-gray-200 bg-white p-2"
                            data-testid="professional-application-approve-specializations-tree"
                        >
                            <SpecializationsSelector
                                items={specializationsData?.items}
                                selectedIds={approveSpecializationIds}
                                onToggleSelected={toggleSelected}
                            />
                        </div>
                        {approveValidationError !== null && (
                            <div
                                className="text-xs text-red-600"
                                data-testid="professional-application-approve-validation-error"
                            >
                                {approveValidationError}
                            </div>
                        )}
                    </div>

                    <Textarea
                        label="Комментарий (необязательно)"
                        value={approveReason}
                        onChange={(e) => setApproveReason(e.target.value)}
                        rows={3}
                        data-testid="professional-application-approve-reason"
                    />

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={closeApprove}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void submitApprove();
                            }}
                            disabled={isApproving}
                            data-testid="professional-application-approve-submit"
                            className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
                        >
                            Одобрить
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isRejectOpen}
                onClose={closeReject}
                title="Отклонить заявку"
                testId="professional-application-reject-modal"
            >
                <div
                    className="space-y-4"
                    data-testid="professional-application-reject-form"
                >
                    <Textarea
                        label={`Причина (минимум ${REASON_MIN_LENGTH} символов)`}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={5}
                        error={rejectReasonError ?? undefined}
                        data-testid="professional-application-reject-reason"
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={closeReject}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void submitReject();
                            }}
                            disabled={isRejecting}
                            data-testid="professional-application-reject-submit"
                            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                        >
                            Отклонить
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
